<template>
    <div class="index" ref="indexElement" />
    <div class="attributefilter" :class="{ 'has-items': hasFilterAttribute }">
        <ArticleAttributeFilter
            ref="attributeFilter"
            :items="offerArticles"
            @selectionChanged="onAttributeSelectionChanged"
        />
    </div>
    <div class="itemlist scrollable" ref="itemListElement" />
    <div class="nofilterresult" v-if="isFilteredToNothing">
        <button type="button" class="removefilters" @click="onRemoveAllFiltersClick">
            {{ trans.order.removeAllFilters }}
        </button>
    </div>
    <ScrollTopButton @click="onScrollTopButtonClick" />
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { CategorySlider } from "@/app/elements/CategorySlider";
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import type { ICustomerArticle, ICustomerCategory } from "@/data/model/Customer";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { Format } from "@/data/util/Format";
    import { renderItemFlagsHtml, renderItemIconsHtml } from "@/shared/helpers/AttributeHelper";
    import { clientLanguage } from "@/shared/localization/language";
    import { DomUtil } from "@/shared/ui/Helper";
    import { Image } from "@/shared/ui/Image";
    import { Ripple } from "@/shared/ui/Ripple";
    import { toMoney, toRate } from "@/shared/utils/format";
    import { computed, onMounted, ref } from "vue";

    import ScrollTopButton from "@/app/components/elements/ScrollTopButton.vue";
    import ArticleAttributeFilter from "@/app/components/order/ArticleAttributeFilter.vue";

    // html refs
    const indexElement = ref<HTMLElement | null>(null);
    const itemListElement = ref<HTMLElement | null>(null);

    let $itemList: JQuery;
    let $index: JQuery;
    let categorySlider: CategorySlider;
    let isScrolled = false;

    let listenToScrollEvents = true;

    const emit = defineEmits<{
        (e: "articleClick", article: ICustomerArticle): void;
        (e: "scrolledChanged", isScrolled: boolean): void;
    }>();

    const offerStore = useOfferStore();
    const identity = useIdentityStore();
    const articleAttribute = useArticleAttributesStore();

    // this overview is the customer one, the kiosk has its own, so the identity alone decides
    // which flags the guest in front of the screen is allowed to see
    const isOperatorView = computed(() => identity.hasOperatorAccess);

    // all articles of the current offer, the filter offers the attributes found on them
    const offerArticles = computed(() => offerStore.itemCategories.flatMap((c) => c.articles));

    // the filter bar only takes up space when there is something to filter by
    const filterAttributes = computed(() =>
        offerArticles.value.flatMap((offerArticle) => articleAttribute.filterAttributesForArticle(offerArticle.articleId))
    );
    const hasFilterAttribute = computed(() => filterAttributes.value.length > 0);

    const attributeFilter = ref<InstanceType<typeof ArticleAttributeFilter> | null>(null);

    let activeIncludeIds: Array<string> = [];
    let activeExcludeIds: Array<string> = [];

    // the offer has categories but the active filter left none of them with an article, so the list is
    // blank and the only way out is to drop the filter again
    const isFilteredToNothing = ref(false);

    // an article has to carry every active include attribute and none of the active exclude ones
    function matchesAttributeFilter(article: ICustomerArticle): boolean {
        if (activeIncludeIds.length === 0 && activeExcludeIds.length === 0) return true;

        const filterAttributeIds = articleAttribute.filterAttributesForArticle(article.articleId).map(a => a.id);

        if (!activeIncludeIds.every((id) => filterAttributeIds.includes(id))) return false;
        if (activeExcludeIds.some((id) => filterAttributeIds.includes(id))) return false;

        return true;
    }

    // geometry of the fixed block above the list, mirrored from OrderView.vue: the view head plus the
    // category slider, which collapse to the slider alone once the list is scrolled, and the attribute
    // filter strip below them when it has buttons. .itemlist starts at the bottom edge of that block, so
    // the sections are measured against the top of the list itself
    const headerHeight = 131;
    const filterBarHeight = 55;

    // a section counts as the current one from the moment its top reaches the top of the list, with a
    // little slack so the section a category jump lands there is picked up reliably
    const currentSectionTolerance = 9;

    // the list reaches from the fixed block down to the bottom bar, and collapsing the block makes it taller. its
    // bottom edge stays put, also while the change is animated, so the height it has with the block expanded is
    // read from there - deciding the collapse by the current height would let it undo itself
    function expandedListHeight(): number {
        const listBottom = $itemList.position().top + $itemList.outerHeight()!;
        return listBottom - (headerHeight + (hasFilterAttribute.value ? filterBarHeight : 0));
    }

    function onRemoveAllFiltersClick() {
        attributeFilter.value?.clearSelection();
    }

    function onAttributeSelectionChanged(selection: { includeIds: Array<string>; excludeIds: Array<string> }) {
        activeIncludeIds = selection.includeIds;
        activeExcludeIds = selection.excludeIds;

        redrawList();
    }

    onMounted(() => {
        initialize();
    });

    function initialize() {
        $index = $(indexElement.value!);
        $itemList = $(itemListElement.value!);

        categorySlider = new CategorySlider().appendTo($index);
        categorySlider.onSelected = (scrollToId) => {
            const cat = $itemList.find('> .section[data-id="' + scrollToId + '"]');
            if (cat.length === 0) {
                return;
            }

            listenToScrollEvents = false;
            // the scroll offset that puts the section at the top of the list, right under the fixed block
            const elementPosition = cat.position().top + $itemList.scrollTop();

            checkScrollCollapse(elementPosition);

            // DomUtil.scrollTo stops 6px short of the offset it is given
            DomUtil.scrollTo($itemList, elementPosition + 6, () => {
                listenToScrollEvents = true;
            });
        };

        $itemList.scroll(() => {
            onScroll();
        });
    }

    function redrawList() {
        $itemList.empty();

        if (
            TastierClient.context.currentOffer === undefined ||
            TastierClient.context.currentOffer.entries.length === 0
        ) {
            $itemList.append('<div class="nodata"><div class="lbl">' + trans.order.noOffer + "</div></div>");
            isFilteredToNothing.value = false;
            categorySlider.setCategories([]);
            $itemList.scrollTop(0);
            updateCart();
            return;
        }

        // categories left without any matching article are not rendered at all
        const itemCategories = TastierClient.context.currentOffer.entries.filter(
            (c) => c.articles.filter(matchesAttributeFilter).length > 0
        );

        // an offer whose categories are all empty leaves nothing to render either, so the active
        // selection has to be checked as well - the button is only an offer to undo that selection
        isFilteredToNothing.value =
            itemCategories.length === 0 && (activeIncludeIds.length > 0 || activeExcludeIds.length > 0);

        for (const itemCategory of itemCategories) {
            renderSection(itemCategory).appendTo($itemList);
        }

        categorySlider
            .setCategories(
                itemCategories.map((c) => {
                    return {
                        id: c.id,
                        name: Format.translate(c.name, clientLanguage()),
                    };
                })
            )
            .moveToFirst();

        updateCart();
        onScroll();
    }

    function renderSection(model: ICustomerCategory): JQuery {
        const section = $(`<div class="section" data-id="${model.id}" />`);

        const header = $('<div class="header" />').appendTo(section);
        if (model.imagePath !== undefined) {
            header
                .addClass("withimage")
                .append(`<div class="background" style="background-image:url(${model.imagePath});"></div>`)
                .append('<div class="blackdim" />');
        } else header.addClass("nameonly");

        const name = $(
            '<div class="name"><h2>' + Format.htmlEscape(Format.translate(model.name, clientLanguage())) + "</h2></div>"
        ).appendTo(header);
        const desc = Format.translate(model.description, clientLanguage());
        if (desc !== undefined)
            name.addClass("twolines").append('<div class="description">' + Format.htmlEscape(desc) + "</div>");

        const articles = $('<div class="articles" />').appendTo(section);

        const visibleArticles = model.articles.filter(matchesAttributeFilter);

        if (visibleArticles.find((c) => c.imagePath !== undefined) !== undefined) articles.addClass("withimage");

        for (const article of visibleArticles) {
            renderArticle(article).appendTo(articles);
        }

        return section;
    }

    function renderArticle(model: ICustomerArticle): JQuery {
        const article = $('<div class="article" />').data("model", model);
        const articlePrice = Helper.sitePrice(model);

        const nameStr = Format.htmlEscape(Format.translate(model.name, clientLanguage()));

        const flagsHtml = renderItemFlagsHtml(model.articleId, isOperatorView.value);
        article.append(flagsHtml);

        const name = $(`<div class="name"></div>`).appendTo(article);
        // the helper renders nothing at all when the article carries no flag this audience may see
        if (flagsHtml !== "") name.addClass("with-flags");

        const nameHeader = $(`<h3>${nameStr}${renderItemIconsHtml(model.articleId, true)}</h3>`).appendTo(name);
        if (articlePrice < 0) {
            nameHeader.addClass("negative-price");
        }

        const desc = Format.translate(model.shortDescription, clientLanguage());
        if (desc !== undefined)
            name.addClass("twolines").append('<div class="description">' + Format.htmlEscape(desc) + "</div>");

        if (model.isOpenPriceEnabled === false) {
            // a price per minute is a rate with up to 5 decimals, the add to cart popover and the cart show it rounded
            const priceText = model.isPricePerMinute ? toRate(articlePrice) : toMoney(articlePrice);
            const articlePriceDiv = $(`<div class="price">${priceText}</div>`).appendTo(name);

            if (articlePrice < 0) {
                articlePriceDiv.addClass("negative-price");
            }
        }

        if (model.imagePath !== undefined) {
            article.addClass("hasimage");

            const imgElement = $(
                `<div class="image"><img src="${model.imagePath}" alt="${nameStr}" data-original-img="${model.originalImagePath}" /></div>`
            );

            imgElement.click((e) => {
                if (model?.originalImagePath === undefined) return;

                e.stopPropagation();
                showImage(model.originalImagePath);
            });

            article.append(imgElement);
        }

        article.append('<div class="line" />');

        Ripple.bind(article);

        article.click((e) => {
            const article = $(e.currentTarget).data("model");
            emit("articleClick", article);
        });

        return article;
    }

    function updateCart() {
        const offer = TastierClient.context.currentOffer;

        $itemList.find("> .section > .articles > .article").each((i, e) => {
            const articleEl = $(e);
            const article: ICustomerArticle = articleEl.data("model");

            const cartEntries =
                offer === undefined || offer.cart === undefined
                    ? []
                    : offer.cart.items.filter((c) => c.articleId === article.articleId);

            if (cartEntries.length === 0) {
                if (articleEl.hasClass("added")) {
                    articleEl
                        .removeClass("added")
                        .delay(400)
                        .queue((a) => {
                            articleEl.find("> .name > .qty").remove();
                            a();
                        });
                }
            } else {
                let qtyContainer = articleEl.find("> .name > .qty");
                if (qtyContainer.length === 0) {
                    qtyContainer = $('<div class="qty" />').prependTo(articleEl.find("> .name"));
                    qtyContainer[0].offsetHeight;
                }

                let sum = 0;
                for (const cartItem of cartEntries) {
                    sum += cartItem.quantity;
                }

                qtyContainer.html(sum + "x");
                articleEl.addClass("added");
            }
        });
    }

    function onScrollTopButtonClick() {
        listenToScrollEvents = false;
        categorySlider!.moveToFirst();

        checkScrollCollapse(0);
        DomUtil.scrollTo($itemList, 0, () => {
            listenToScrollEvents = true;
        });
    }

    function onScroll() {
        if (!listenToScrollEvents) return;

        const currentOffset = $itemList.scrollTop();

        //get the last section that has reached the header block
        const sections = new Array<{ el: JQuery; offset: number }>();
        $itemList.children(".section").each((i, e) => {
            const el = $(e);
            const offset = el.position().top;
            if (offset <= currentSectionTolerance) sections.push({ el: el, offset: offset });
        });

        if (sections.length > 0) {
            const current = sections.sort((a, b) => b.offset - a.offset)[0];
            categorySlider.moveScrollTo(current.el.attr("data-id"));
        }

        checkScrollCollapse(currentOffset);
    }

    function checkScrollCollapse(forOffset: number) {
        const shouldCollapse = forOffset > 80 && expandedListHeight() + 52 < forOffset;

        if (!isScrolled && shouldCollapse) {
            emit("scrolledChanged", true);
            isScrolled = true;
        } else if (isScrolled && !shouldCollapse) {
            emit("scrolledChanged", false);
            isScrolled = false;
        }
    }

    function setIsLoading() {
        isFilteredToNothing.value = false;
        $itemList.html(`<div class="loading"><div class="loader"></div>${trans.order.loadingOffer}</div>`);
    }

    async function showImage(imagePath: string) {
        // todo: stolen from CovidController - refactor to a shared function
        const parent = $(".app");

        const dim = $('<div class="dim" />').appendTo(parent);

        dim[0].offsetHeight;
        dim.addClass("shown");

        const pop = $('<div class="identitydocmodal"></div>').appendTo(parent);
        const s = await Image.preloadImage(imagePath);
        let img: JQuery;
        if (s !== undefined) img = $(s).appendTo(pop);
        else img = $('<img src="' + imagePath + '" />').appendTo(pop);

        pop[0].offsetHeight;

        const imgElement = img[0] as HTMLImageElement;
        const imgHeight = (parent.width() / imgElement.width) * imgElement.height;
        pop.css({
            height: imgHeight,
            "margin-top": imgHeight / -2,
        });

        pop.addClass("shown");
        pop.append('<div class="close">' + trans.general.close + "</div>");

        const hide = () => {
            dim.removeClass("shown");
            pop.removeClass("shown")
                .delay(400)
                .queue((e) => {
                    dim.remove();
                    pop.remove();

                    e();
                });
        };

        dim.click(() => {
            hide();
        });

        pop.click(() => {
            hide();
        });
    }

    function reset() {
        $itemList.empty();
        isFilteredToNothing.value = false;
        categorySlider.setCategories([]);
    }

    defineExpose({
        redrawList,
        updateCart,
        setIsLoading,
        reset,
    });
</script>
